import { Injectable } from '@angular/core';
import { Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject } from 'rxjs';
import { NotifierService } from 'angular-notifier';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private inactivityTime = 30 * 60 * 1000; // 30 minutes
    private timeoutId: any;

    private isLoggedInSubject = new BehaviorSubject<boolean>(this.checkLoginStatus());

    // 🔁 getter observable
    isLoggedIn$ = this.isLoggedInSubject.asObservable();

    // 🔁 interne : pour émettre
    private checkLoginStatus(): boolean {
        const token = this.getToken();
        if (!token) return false;
        try {
        const decoded: any = jwtDecode(token);
        return decoded.exp && decoded.exp > Date.now() / 1000;
        } catch {
        return false;
        }
    }

    constructor(
      private router: Router,
      private notifier: NotifierService,
      @Inject(PLATFORM_ID) private platformId: Object
    ) {
      if (this.isBrowser() && this.isLoggedIn()) {
        this.startInactivityTimer();
      }
    
      if (this.isBrowser()) {
        this.setupActivityListeners();
      }
    }
    
    getToken() {
      if (this.isBrowser()) {
        const user = JSON.parse(localStorage.getItem('goca_users') || '{}');
        return user?.jwt;
      }
      return null;
    }    

    isLoggedIn() {
        const token = this.getToken();
        if (!token) return false;
    
        try {
          const decoded: any = jwtDecode(token);
          const now = Date.now() / 1000;
    
          if (decoded.exp && decoded.exp < now) {
            this.logOut(true);
            return false;
          }
    
          return true;
        } catch (e) {
          this.logOut(true);
          return false;
        }
    }

    getUser() {
      if (this.isBrowser()) {
        const users = JSON.parse(localStorage.getItem('goca_users') || '{}');
        return users?.user;
      }
      return null;
    }    

    getUserId() {
        const user = this.getUser();
        return user?.id;
    }      

    logOut_old() {
        localStorage.removeItem("goca_users");
        this.clearInactivityTimer();
        //this.router.navigate(['/sign-in']);
    }

    private isBrowser(): boolean {
      return isPlatformBrowser(this.platformId);
    }    

    logOut(isExpired: boolean = false) {
      if (this.isBrowser()) {
        localStorage.removeItem("goca_users");
      }
      this.clearInactivityTimer();
      this.isLoggedInSubject.next(false);
    
      if (isExpired) {
        this.notifier.notify('warning', '⏳ Votre session a expiré pour cause d’inactivité.');
      } else {
        this.notifier.notify('success', '✅ Vous vous êtes déconnecté avec succès.');
      }
    }
    
    logIn(userJwt: string) {
      if (this.isBrowser()) {
        localStorage.setItem("goca_users", userJwt);
      }
      this.isLoggedInSubject.next(true);
    }

    getAuthHeader() {
        return {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        };
    }

    /** ⏱ GESTION DU TEMPS D'INACTIVITÉ **/

    startInactivityTimer() {
      if (this.isBrowser()) {
        this.clearInactivityTimer();
        this.timeoutId = setTimeout(() => {
          this.logOut(true);
        }, this.inactivityTime);
      }
    }    

    clearInactivityTimer() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
    }

    setupActivityListeners() {
      if (!this.isBrowser()) return;
    
      ['click', 'mousemove', 'keydown'].forEach(event => {
        window.addEventListener(event, () => {
          if (this.isLoggedIn()) {
            this.startInactivityTimer();
          }
        });
      });
    }    
}